import React from 'react';
import { useTranslate } from 'hooks/useTranslate';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import * as clipboard from 'clipboard-polyfill';
import Box from '@mui/material/Box';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import LinearProgress from 'components/LinearProgress';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { styled } from '@mui/system';
import { getLanguage } from 'translation';
import DatePickerComponent from 'components/DatePicker';
import { startPayment, createOrder, getOrders } from 'actions/payment';
import CoastsIcon from 'assets/icons/coasts.svg?react';
import CopyIcon from 'assets/icons/copy_icon.svg?react';
import OrIcon from 'assets/icons/orIcon.svg?react';
import { auth } from 'services/firebase';
import { isAuthenticated } from 'helpers/authToken';

const TemplateText = styled(Typography)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  color: theme.palette.textPrimary.main,
  marginBottom: 20,
  '& svg': {
    width: 35,
    marginRight: 10,
    flex: 1
  },
  '& span': {
    flex: 8
  }
}));

const ButtonStyled = styled(Button)(({ theme }) => ({
  marginBottom: 20,
  padding: '14px 5px',
  width: '100%',
  borderRadius: 8, // Match mobile sticky button
  [theme.breakpoints.down('md')]: {
    marginBottom: 15,
    marginTop: 20
  },
  [theme.breakpoints.down('sm')]: {
    display: 'none' // Hide on mobile since we have the sticky button
  }
}));

const DialogContentWrapper = styled(DialogContent)(({ theme }) => ({
  padding: 30,
  paddingBottom: 0,
  display: 'flex',
  justifyContent: 'space-between',
  gap: 50,
  [theme.breakpoints.down('sm')]: {
    padding: 20,
    gap: 0,
    display: 'block'
  }
}));

const DialogTitleWrapper = styled(DialogTitle)(() => ({
  padding: 0,
  marginBottom: 20
}));

const DialogStyled = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: 25,
    [theme.breakpoints.down('sm')]: {
      borderRadius: 0
    }
  }
}));

const ReceiptText = styled(Typography)(() => ({
  fontSize: 18,
  lineHeight: '27px',
  fontWeight: 600,
  marginBottom: 25
}));

const LeftDialogSidebar = styled('div')(({ theme }) => ({
  maxWidth: 320,
  [theme.breakpoints.down('sm')]: {
    maxWidth: '100%'
  }
}));

const RightDialogSidebar = styled('div')(({ theme }) => ({
  maxWidth: 450,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start', // Align items to start so inputs can be narrower
  [theme.breakpoints.down('sm')]: {
    maxWidth: '100%'
  }
}));

const TextFieldStyled = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-root': {
    minHeight: '40px'
  },
  '& .MuiInputBase-input': {
    padding: '2px 14px'
  },
  '& .MuiInputLabel-shrink': {
    backgroundColor: '#ffffff',
    padding: '0 6px'
  },
  '& .MuiOutlinedInput-notchedOutline legend': {
    maxWidth: 0
  },
  [theme.breakpoints.up('sm')]: {
    maxWidth: 420, // Match button width on desktop
    width: '100%'
  }
}));

const PaymentButton = styled(Button)(() => ({
  marginBottom: 10,
  borderRadius: 8 // Match mobile sticky button
}));

const PaymentCaptionText = styled(Typography)(({ theme }) => ({
  fontSize: 14, // Match PaymentNote font size
  lineHeight: '20px', // Match PaymentNote line height
  marginBottom: 10,
  color: theme.palette.grey[200], // Same color as PaymentNote
  display: 'block',
  textAlign: 'center',
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    maxWidth: 420 // Match input width on desktop
  }
}));

const PaymentFormHeadline = styled(Typography)(() => ({
  marginBottom: 10
}));

const PaymentNote = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  lineHeight: '20px',
  color: theme.palette.grey[200],
  marginTop: 10,
  marginBottom: 10
}));

const DialogCloseButton = styled(Button)(({ theme }) => ({
  color: theme.palette.grey[200],
  alignSelf: 'center',
  marginTop: 20,
  marginBottom: 40,
  border: '1px solid transparent',
  borderRadius: 8,
  borderBottom: `1px solid ${theme.palette.grey[200]}`,
  padding: 0,
  '&:hover': {
    backgroundColor: 'transparent'
  }
}));

const ButtonCopy = styled(Button)(({ theme }) => ({
  color: 'rgba(82, 82, 82, 1)',
  padding: '6px 5px',
  fontSize: 16,
  lineHeight: '22px',
  fontWeight: 500,
  boxShadow: 'none',
  marginTop: 10,
  borderRadius: 8, // Match mobile sticky button
  [theme.breakpoints.down('sm')]: {
    fontSize: 12,
    padding: '5px 0'
  }
}));

const SuccessMessage = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontSize: 16,
  lineHeight: '22px',
  fontWeight: 500,
  textAlign: 'center',
  width: '100%'
}));

const FilterItemWrapper = styled('div')(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  marginBottom: 10,
  gap: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    maxWidth: 420 // Match button width on desktop
  },
  '& .MuiStack-root': {
    margin: 0,
    width: '100%',
    padding: 0
  }
}));


const ActionsWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    maxWidth: 380, // Match input width on desktop
    paddingRight: 0 // Remove padding to match input width exactly
  },
  [theme.breakpoints.down('sm')]: {
    paddingRight: 0
  }
}));

const FormControlStyled = styled(FormControl)(({ theme }) => ({
  marginBottom: 20,
  '& label': {
    paddingLeft: 0,
    display: 'inline-flex',
    justifyContent: 'flex-start',
    alignItems: 'start',
    marginBottom: 10,
    fontSize: 14,
    lineHeight: '20px',
    '& span': {
      fontSize: 14,
      lineHeight: '20px',
      '& b': {
        fontSize: 14,
        lineHeight: '20px',
        fontWeight: 600
      }
    }
  },
  '& span': {
    paddingTop: 0
  },
  '& svg': {
    marginRight: 7
  }
}));

const PAYMENT_FIELDS = ['fullName', 'age', 'phone', 'email'];
const CONTACT_METHODS = ['phone', 'telegram', 'viber', 'whatsapp'];

// SessionStorage key for form data
const getSessionStorageKey = (consultantId) => `consultantPayment_${consultantId}`;

const ConsultantPayment = ({ info }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const sessionStorageKey = React.useMemo(() => getSessionStorageKey(info?._id), [info?._id]);
  
  // Load initial state from sessionStorage
  const loadFromSessionStorage = React.useCallback(() => {
    if (!info?._id) return null;
    const key = getSessionStorageKey(info._id);
    try {
      const saved = sessionStorage.getItem(key);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      console.error('Error loading from sessionStorage:', error);
    }
    return null;
  }, [info?._id]);

  const savedData = React.useMemo(() => loadFromSessionStorage(), [loadFromSessionStorage]);
  
  // Check if URL has order parameter and open modal if it does
  const urlHasOrderParam = React.useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('order') === 'true';
  }, [location.search]);
  
  const [consultationType, setConsultationType] = React.useState(
    savedData?.consultationType || (info?.services || [])[0]?.code
  );
  const [paymentPending, setPaymentPending] = React.useState(false);
  const [openPayment, setOpenPayment] = React.useState(urlHasOrderParam);
  const [paymentFields, setPaymentFields] = React.useState(savedData?.paymentFields || {});
  const [errors, setErrors] = React.useState([]);
  const [emptyFields, setEmptyFields] = React.useState([]);
  const [isCopied, setIsCopied] = React.useState(false);
  const [activeOrder, setActiveOrder] = React.useState(null);
  const [openPicker, setOpenPicker] = React.useState(false);
  const [contactMethod, setContactMethod] = React.useState(savedData?.contactMethod || 'phone');
  const user = useSelector((state) => state.app.user);
  const isMobile = useSelector((state) => state.app.isMobile);

  const [lang] = React.useState(getLanguage().toUpperCase());
  const dispatch = useDispatch();
  const t = useTranslate('Consultant');

  const paymentKeys = React.useMemo(() => PAYMENT_FIELDS, []);

  const userName = React.useMemo(
    () => `${info[`firstName${lang}`]} ${info[`lastName${lang}`]}`,
    [lang, info]
  );

  // Save to sessionStorage whenever form data changes (debounced to avoid too many writes)
  React.useEffect(() => {
    if (!sessionStorageKey) return;
    
    // Debounce the save to avoid too many writes
    const timeoutId = setTimeout(() => {
      try {
        const dataToSave = {
          consultationType,
          paymentFields,
          contactMethod
        };
        sessionStorage.setItem(sessionStorageKey, JSON.stringify(dataToSave));
      } catch (error) {
        console.error('Error saving to sessionStorage:', error);
      }
    }, 300); // 300ms debounce
    
    return () => clearTimeout(timeoutId);
  }, [consultationType, paymentFields, contactMethod, sessionStorageKey]);

  const handleChangeType = (event) => {
    const newType = event.target.value;
    setConsultationType(newType);
    import('services/analytics').then(({ default: A }) => A.event('ChangeConsultationType', {
      serviceCode: newType
    }));
  };

  const handleChangePaymentField = React.useCallback(
    (event) => {
      const { name, value } = event.target;
      setPaymentFields({ ...paymentFields, [name]: value });
      // Clear error for this field when user starts editing
      if (errors.includes(name)) {
        setErrors(prev => prev.filter(err => err !== name));
        setEmptyFields(prev => prev.filter(field => field !== name));
      }
    },
    [paymentFields, errors]
  );

  const getActiveOrder = React.useCallback(
    (orders) => {
      return (orders || []).find((order) => {
        const activePayment =
          order.type === 'consultantSession' &&
          order.serviceId === info?._id &&
          Number(order?.subServiceId) === Number(consultationType) &&
          order?.invoicePageUrl &&
          order?.invoice?.status === 'created';
        return activePayment;
      });
    },
    [info, consultationType]
  );

  const normalizeOrders = React.useCallback((payload) => {
    if (!payload || payload instanceof Error) {
      return [];
    }

    if (Array.isArray(payload)) {
      return payload;
    }

    if (Array.isArray(payload?.data)) {
      return payload.data;
    }

    return [];
  }, []);

  const handleCheckOrders = React.useCallback(async () => {
    // Only check orders if user is already authenticated
    // Skip anonymous sign-in to avoid Firebase restrictions
    if (!isAuthenticated() || !auth.currentUser) {
      setPaymentPending(false);
      return;
    }

    setPaymentPending(true);

    try {
      const orders = await dispatch(getOrders());

      setPaymentPending(false);

      const normalizedOrders = normalizeOrders(orders);

      const orderCreated = getActiveOrder(normalizedOrders);

      if (orderCreated) {
        setActiveOrder(orderCreated);
        return;
      }
    } catch (error) {
      setPaymentPending(false);
      // Silently fail - user can still create new order
      console.error('Error checking orders:', error);
    }
  }, [dispatch, getActiveOrder, normalizeOrders]);

  const handleOpenPayment = React.useCallback((event) => {
    import('services/analytics').then(({ default: A }) => A.event('GetConsultationClick'));
    setActiveOrder(null);
    // Blur the button to prevent focus conflict with aria-hidden
    if (event?.currentTarget) {
      event.currentTarget.blur();
    }
    setOpenPayment(true);
    handleCheckOrders();
    
    // Update URL with order parameter
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('order', 'true');
    navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
  }, [handleCheckOrders, location, navigate]);

  const handlePaymentWidgetLink = React.useCallback((link, redirect) => {
    if (redirect) {
      window.location.href = link;
    } else {
      clipboard.writeText(link).then(() => {
        setIsCopied(true);
        setTimeout(() => {
          setIsCopied(false);
        }, 2000);
      });
    }
  }, []);

  const handleContinuePayment = React.useCallback(
    (redirect) => {
      handlePaymentWidgetLink(activeOrder?.invoicePageUrl, redirect);
    },
    [activeOrder, handlePaymentWidgetLink]
  );

  const handleClosePaymentDialog = React.useCallback(() => {
    setOpenPayment(false);
    
    // Remove order parameter from URL
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete('order');
    const newSearch = searchParams.toString();
    const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;
    navigate(newUrl, { replace: true });
  }, [location, navigate]);

  // Clear sessionStorage when dialog is closed after successful payment
  React.useEffect(() => {
    if (!openPayment && activeOrder) {
      // Dialog was closed and there was an active order, clear storage
      if (sessionStorageKey) {
        try {
          sessionStorage.removeItem(sessionStorageKey);
        } catch (error) {
          console.error('Error clearing sessionStorage:', error);
        }
      }
    }
  }, [openPayment, activeOrder, sessionStorageKey]);

  const handleStartPayment = React.useCallback(
    async (redirect) => {
      if (paymentPending) return;

      setErrors([]);
      setEmptyFields([]);

      // Validate all fields including email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const phoneRegex = /^[\d\s+\-()]+$/;
      const errors = [];
      const empty = [];
      
      paymentKeys.forEach((field) => {
        if (field === 'phone') {
          const phoneValue = paymentFields[field] || '';
          if (!phoneValue) {
            empty.push(field);
            errors.push(field);
          } else if (!phoneRegex.test(phoneValue)) {
            errors.push(field);
          }
        } else if (field === 'email') {
          const emailValue = paymentFields[field] || '';
          if (!emailValue) {
            empty.push(field);
            errors.push(field);
          } else if (!emailRegex.test(emailValue)) {
            errors.push(field);
          }
        } else {
          if (!paymentFields[field]) {
            empty.push(field);
            errors.push(field);
          }
        }
      });

      if (errors.length) {
        setErrors(errors);
        setEmptyFields(empty);
        return;
      }

      setPaymentPending(true);

      try {
        // Auth flows via the httpOnly session cookie (credentials: 'include' in
        // the api layer) for verified users, or via the legacy localStorage
        // `token` header fallback for anonymous Firebase users. Either way, no
        // explicit token plumbing is needed here — guests hit these endpoints
        // without auth and the backend allows it via the guest-token flow.
        const order = await dispatch(
          createOrder({
            type: 'consultantSession',
            options: {
              consultantId: info._id,
              serviceCode: consultationType,
              email: paymentFields.email, // Required for order creation
              additionalOrderInfo: {
                ...paymentFields,
                language: getLanguage()
              }
            }
          })
        );

        if (!order || order instanceof Error) {
          setPaymentPending(false);
          return;
        }

        const result = await dispatch(startPayment(order._id));

        setPaymentPending(false);

        if (!result || result instanceof Error) return;

        // Clear sessionStorage after successful payment initiation
        if (sessionStorageKey) {
          try {
            sessionStorage.removeItem(sessionStorageKey);
          } catch (error) {
            console.error('Error clearing sessionStorage:', error);
          }
        }

        handlePaymentWidgetLink(result.pageUrl, redirect);
      } catch (error) {
        setPaymentPending(false);
        console.error('Error in payment flow:', error);
        setErrors(['email']); // Show error on email field
      }
    },
    [
      dispatch,
      consultationType,
      paymentPending,
      paymentKeys,
      info,
      handlePaymentWidgetLink,
      paymentFields,
      sessionStorageKey
    ]
  );


  const handleReNewOrder = React.useCallback(() => {
    setActiveOrder(null);
  }, []);

  // Auto-fill form fields from authenticated user data
  React.useEffect(() => {
    if (user) {
      const fullName = user?.displayName || '';

      // Only set user data if fields are empty (don't override saved data or user input)
      setPaymentFields((prev) => {
        const updates = {};
        
        // Fill fullName if empty
        if (!prev.fullName && fullName) {
          updates.fullName = fullName;
        }
        
        // Fill email if empty
        if (!prev.email && user?.email) {
          updates.email = user.email;
        }
        
        // Fill phone if empty
        if (!prev.phone && user?.phoneNumber) {
          updates.phone = user.phoneNumber;
        }
        
        // Only update if there are changes
        if (Object.keys(updates).length > 0) {
          return {
            ...prev,
            ...updates
          };
        }
        
        return prev;
      });
    }
  }, [user]);

  // Restore data from sessionStorage when modal opens
  React.useEffect(() => {
    if (openPayment && sessionStorageKey) {
      const saved = loadFromSessionStorage();
      if (saved) {
        // Restore paymentFields if they exist in storage
        if (saved.paymentFields && Object.keys(saved.paymentFields).length > 0) {
          setPaymentFields(prev => ({ ...prev, ...saved.paymentFields }));
        }
        // Restore consultationType if it exists in storage
        if (saved.consultationType) {
          setConsultationType(saved.consultationType);
        }
        // Restore contactMethod if it exists in storage
        if (saved.contactMethod) {
          setContactMethod(saved.contactMethod);
        }
      }
    }
  }, [openPayment, sessionStorageKey, loadFromSessionStorage]);

  if (!info) return null;

  return (
    <>
      <FormControlStyled>
        <RadioGroup
          aria-labelledby={t('PaymentText')}
          name="amount"
          value={consultationType}
          onChange={handleChangeType}>
          {(info?.services || []).map((service) => (
            <FormControlLabel
              key={service._id}
              value={service.code}
              control={<Radio disableRipple={true} />}
              label={
                <>
                  <b>
                    {service.price} {t('Currency')}
                  </b>
                  {': '}
                  {service[`name${lang}`]}
                </>
              }
            />
          ))}
        </RadioGroup>
      </FormControlStyled>

      <ButtonStyled
        variant="contained"
        color="primary"
        onClick={handleOpenPayment}
        data-cta="get-consultation">
        {t('GetConsultation')}
      </ButtonStyled>

      <DialogStyled
        open={openPayment}
        onClose={handleClosePaymentDialog}
        fullWidth={true}
        maxWidth={'md'}
        fullScreen={isMobile}
        scroll="body"
        disableAutoFocus={false}
        disableEnforceFocus={false}
        disableRestoreFocus={false}>
        <DialogContentWrapper>
          <LeftDialogSidebar>
            <DialogTitleWrapper component={'div'}>
              <Typography variant="h3" sx={{ fontSize: { xs: '28px', sm: '32px', md: '36px' }, lineHeight: 1.4 }}>{t('PaymentText')}</Typography>
            </DialogTitleWrapper>

            <ReceiptText variant="body1">
              {t('ReceiptText', {
                userName,
                sessionName: ((info?.services || []).find(
                  (service) => service.code === Number(consultationType)
                ) || {})[`name${lang}`]
              })}
            </ReceiptText>

            <TemplateText>
              <CoastsIcon />
              <span>
                <b>
                  {
                    (
                      (info?.services || []).find(
                        (service) => service.code === Number(consultationType)
                      ) || {}
                    ).price
                  }{' '}
                  {t('Currency')}
                </b>
              </span>
            </TemplateText>

            <PaymentNote variant="body2">
              {t('PaymentNote')}
            </PaymentNote>
          </LeftDialogSidebar>
          {activeOrder ? (
            <RightDialogSidebar>
              <PaymentFormHeadline>{t('ContinuePaymentHeadline')}</PaymentFormHeadline>
              <PaymentFormHeadline>{t('ContinuePaymentText')}</PaymentFormHeadline>
              <PaymentButton
                variant="contained"
                color="primary"
                fullWidth={true}
                onClick={() => handleContinuePayment(true)}
                aria-label={t('ContinuePayment')}>
                {t('ContinuePayment')}
              </PaymentButton>

              <Box sx={{ mb: 1 }}>
                <OrIcon width={'100%'} />
              </Box>
              <Box sx={{ mb: 2 }}>
                <PaymentButton
                  variant="contained"
                  color="primary"
                  fullWidth={true}
                  onClick={handleReNewOrder}
                  aria-label={t('ToPayment')}>
                  {t('CreateNewPayment')}
                </PaymentButton>
              </Box>
            </RightDialogSidebar>
          ) : (
            <RightDialogSidebar>
              {paymentKeys.map((field) => {
                if (field === 'age') {
                  const countAge = (date) => {
                    if (!date) return '';
                    const birthDate = new Date(date);
                    const diff = Date.now() - birthDate.getTime();
                    const ageDate = new Date(diff);
                    return Math.abs(ageDate.getUTCFullYear() - 1970);
                  };

                  return (
                    <React.Fragment key={field}>
                      <FilterItemWrapper>
                        <Typography variant="body2" sx={{ mb: 0.25, fontSize: 14, lineHeight: 1.2, color: 'primary.main' }}>
                          {t('ageDescription')}
                        </Typography>
                        <DatePickerComponent
                          open={openPicker}
                          onOpen={() => setOpenPicker(true)}
                          onClose={() => setOpenPicker(false)}
                          name="birthDate"
                          value={paymentFields['birthDate'] || null}
                          displayValue={paymentFields['age'] || ''}
                          fullWidth
                          error={errors.includes(field)}
                          helperText={errors.includes(field) ? (emptyFields.includes(field) ? t('FillFieldFirst') : t('InvalidField')) : ''}
                          placeholder={t(`${field}Placeholder`)}
                          onChange={(value) => {
                            if (!value) return '';
                            const birthDateStr = dayjs(value).format('YYYY-MM-DD');
                            setPaymentFields({
                              ...paymentFields,
                              birthDate: birthDateStr,
                              [field]: countAge(birthDateStr)
                            });
                            // Clear error for this field when user selects a date
                            if (errors.includes(field)) {
                              setErrors(prev => prev.filter(err => err !== field));
                              setEmptyFields(prev => prev.filter(f => f !== field));
                            }
                          }}
                        />
                      </FilterItemWrapper>
                    </React.Fragment>
                  );
                }

                if (field === 'phone') {
                  const handlePhoneChange = (event) => {
                    const value = event.target.value;
                    // Only allow numbers, +, (), -, and spaces
                    if (value === '' || /^[\d\s+\-()]*$/.test(value)) {
                      setPaymentFields({ ...paymentFields, [field]: value });
                      // Clear error for this field when user starts editing
                      if (errors.includes(field)) {
                        setErrors(prev => prev.filter(err => err !== field));
                        setEmptyFields(prev => prev.filter(f => f !== field));
                      }
                    }
                  };

                  return (
                    <FilterItemWrapper key={field}>
                      <Typography variant="body2" sx={{ mb: 0.25, fontSize: 14, lineHeight: 1.2, color: 'primary.main' }}>
                        {t(`${field}Placeholder`)}
                      </Typography>
                      <TextFieldStyled
                        name={field}
                        type="tel"
                        fullWidth={true}
                        placeholder={t('phoneInputPlaceholder')}
                        value={paymentFields[field] || ''}
                        error={errors.includes(field)}
                        onChange={handlePhoneChange}
                        helperText={errors.includes(field) ? (emptyFields.includes(field) ? t('FillFieldFirst') : t('InvalidField')) : ''}
                        slotProps={{
                          htmlInput: { maxLength: 100, 'aria-label': t(`${field}Placeholder`) }
                        }}
                      />
                    </FilterItemWrapper>
                  );
                }

                if (field === 'email') {
                  return (
                    <React.Fragment key={field}>
                      <FilterItemWrapper>
                        <Typography variant="body2" sx={{ mb: 0, fontSize: 14, lineHeight: 1.2, color: 'primary.main' }}>
                          {t('HowToContactYou')}
                        </Typography>
                        <FormControl fullWidth>
                          <Select
                            value={contactMethod}
                            onChange={(e) => setContactMethod(e.target.value)}
                            displayEmpty
                            sx={{
                              '& .MuiInputBase-root': {
                                minHeight: '40px'
                              },
                              '& .MuiSelect-select': {
                                padding: '2px 14px',
                                minHeight: '40px',
                                display: 'flex',
                                alignItems: 'center',
                                boxSizing: 'border-box'
                              },
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderRadius: '4px'
                              }
                            }}>
                            {CONTACT_METHODS.map((method) => (
                              <MenuItem key={method} value={method}>
                                {t(`ContactMethod${method.charAt(0).toUpperCase() + method.slice(1)}`)}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </FilterItemWrapper>
                      <FilterItemWrapper>
                        <Typography variant="body2" sx={{ mb: 0, fontSize: 14, lineHeight: 1.2, color: 'primary.main' }}>
                          {t(`${field}Placeholder`)}
                        </Typography>
                        <TextFieldStyled
                          name={field}
                          type="email"
                          placeholder={t('emailInputPlaceholder')}
                          fullWidth={true}
                          value={paymentFields[field] || ''}
                          error={errors.includes(field)}
                          helperText={errors.includes(field) ? (emptyFields.includes(field) ? t('FillFieldFirst') : t('InvalidField')) : ''}
                          onChange={handleChangePaymentField}
                          slotProps={{
                            htmlInput: { maxLength: 100, 'aria-label': t(`${field}Placeholder`) }
                          }}
                        />
                      </FilterItemWrapper>
                    </React.Fragment>
                  );
                }

                return (
                    <FilterItemWrapper key={field}>
                      <Typography variant="body2" sx={{ mb: 0, fontSize: 14, color: 'primary.main' }}>
                        {t(`${field}Placeholder`)}
                      </Typography>
                    <TextFieldStyled
                      name={field}
                      placeholder={field === 'fullName' ? t('fullNameInputPlaceholder') : t(`${field}Placeholder`)}
                      fullWidth={true}
                      value={paymentFields[field] || ''}
                      error={errors.includes(field)}
                      helperText={errors.includes(field) ? (emptyFields.includes(field) ? t('FillFieldFirst') : t('InvalidField')) : ''}
                      onChange={handleChangePaymentField}
                      slotProps={{
                        htmlInput: { maxLength: 100, 'aria-label': t(`${field}Placeholder`) }
                      }}
                    />
                  </FilterItemWrapper>
                );
              })}

              <ActionsWrapper>
                <PaymentButton
                  variant="contained"
                  color="primary"
                  fullWidth={true}
                  onClick={() => handleStartPayment(true)}
                  aria-label={t('ToPayment')}
                  sx={{ mt: 2 }}>
                  {t('ToPayment')}
                </PaymentButton>

                <PaymentCaptionText variant="caption">{t('PaymentActionText')}</PaymentCaptionText>

                <OrIcon width={'100%'} />

                <ButtonCopy
                  variant="contained"
                  color="primary"
                  fullWidth={true}
                  onClick={() => handleStartPayment(false)}
                  aria-label={t('CopyPaymentLink')}
                  endIcon={<CopyIcon />}>
                  {t('CopyPaymentLink')}
                </ButtonCopy>

                {isCopied ? <SuccessMessage>{t('Copied')}</SuccessMessage> : null}

                <DialogCloseButton
                  color="primary"
                  variant="text"
                  size="small"
                  onClick={handleClosePaymentDialog}
                  aria-label={t('Close')}>
                  {t('Close')}
                </DialogCloseButton>
              </ActionsWrapper>
              {paymentPending ? <LinearProgress /> : null}
            </RightDialogSidebar>
          )}
        </DialogContentWrapper>
      </DialogStyled>

    </>
  );
};

export default ConsultantPayment;
